import { createApp } from 'vue';
import { createRouter, createWebHashHistory } from 'vue-router';
import { createI18n } from 'vue-i18n';
import { init as initSentry, browserTracingIntegration, replayIntegration } from '@sentry/vue';
import { version } from '../package.json';
import messages from './translations';
import HomePage from './pages/HomePage.vue';
import ReservePage from './pages/ReservePage.vue';
import OptionsPage from './pages/OptionsPage.vue';
import App from './App.vue';

const query = new URLSearchParams(window.location.search);
// #4774: use `||` (not `??`) so an empty `?resort=`/`?workspace=` value falls back to the
// default instead of leaving an empty ref that matches no route and renders a blank app.
const selectedResort = query.get('resort') || query.get('workspace') || 'thermenresorts';

const routes = [
	// #3352: On resort websites we never show the HomePage view with resport choices.
	// Else the user will first ze a list of resorts and then a second later the products.
	selectedResort === 'thermenresorts'
		? { path: '/', name: 'home', component: HomePage }
		: { path: '/', name: 'home', redirect: { name: 'resort-date', params: { resort: selectedResort } } },

	// #4774: Always-reachable resort-choice fallback. Pages redirect here when the resort param
	// is not a valid workspace ref, so an invalid/mis-embedded resort can never crash the widget.
	{ path: '/choose', name: 'resort-choice', component: HomePage },

	// #1224: Forcing old view towards new view.
	{
		path: '/resort/:resort/:date?',
		name: 'resort-date',
		redirect: (to) => {
			return {
				name: 'resort-reserve',
				params: { resort: to.params.resort, date: to.params.date ? to.params.date : 'none' },
			};
		},
	},
	{ path: '/resort/:resort/:date/reserve/:card_id?', component: ReservePage, name: 'resort-reserve' },
	{ path: '/resort/:resort/:date/reserve/:productset_id/options', component: OptionsPage, name: 'resort-options' },
];

const router = createRouter({
	history: createWebHashHistory(import.meta.env.BASE_URL),
	routes,
});

const locale = () => {
	const query = new URLSearchParams(window.location.search);
	const queryLang = query.get('lang');
	switch (queryLang) {
		case 'nl':
		case 'nl-NL':
		case 'nl_NL':
			return 'nl-NL';
		case 'en':
		case 'en-US':
		case 'en_US':
		case 'en-GB':
		case 'en_GB':
		case 'en-EN':
		case 'en_EN':
			return 'en-US';
		case 'de':
		case 'de-DE':
		case 'de_DE':
			return 'de-DE';
		case 'fr':
		case 'fr-FR':
		case 'fr-BE':
		case 'fr-CA':
		case 'fr-CH':
		case 'fr-LU':
		case 'fr-MC':
		case 'fr_FR':
		case 'fr_BE':
		case 'fr_CA':
		case 'fr_CH':
		case 'fr_LU':
		case 'fr_MC':
			return 'fr-FR';
		default:
			return 'nl-NL';
	}
};

const i18n = createI18n({
	locale: locale(),
	fallbackLocale: 'en-GB',
	messages,
	legacy: false,
});

const app = createApp(App);

if (!import.meta.env.DEV) {
	initSentry({
		app,
		dsn: 'https://b13df1b89c3dfc236fb32f2ecdc488fe@o398254.ingest.us.sentry.io/4507129556500480',
		environment: import.meta.env.MODE,
		integrations: [
			browserTracingIntegration(),
			replayIntegration({
				maskAllText: false,
				blockAllMedia: false,
			}),
		],
		ignoreErrors: [
			// Implemented due: https://cube-nl.sentry.io/issues/5301118087/
			// Caused by Sentry itself, found this problem on:
			// https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062
			'Non-Error promise rejection captured',
			'Non-Error exception captured',
			'Object captured as promise',
			// Implemented due: https://cube-nl.sentry.io/issues/5292934929/
			// Whenever someone is clicking fast through the calendar,
			//  this will trigger a cancellation on the first request
			'Request aborted',

			'AxiosError',
			'Rage Click',
			'(evaluating ', // Common pattern for node_modules/ packages
			'change_ua',
		],
		denyUrls: [
			/rum-static\.pingdom\.net\/.*\.js$/,
			/consent\.cookiebot\.com\/.*\.js$/,
			/googletagmanager\.com\/.*\.js$/,
			/static\.zdassets\.com\/.*\.js$/,
			/edge\.marker\.io\/.*\.js$/,
			/cdn-4\.convertexperiments\.com\/.*\.js$/,
			/cdn\.seamly-app\.com\/.*\.js$/,
		],
		tracesSampleRate: 1.0,
		replaysSessionSampleRate: 0.1,
		replaysOnErrorSampleRate: 1.0,
	});
}

app.use(router);
app.use(i18n);

app.mount('#app');

console.log(`Thermen Preflow (v${version}) (${locale()})`);
